import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { TRADITIONS } from "@/data/content";
import { COMPARE, COMPARE_DIMENSIONS } from "@/data/traditionDeep";
import { Reveal, Eyebrow } from "@/components/common";

const ALL = Object.keys(TRADITIONS);

export default function Compare() {
  const [selected, setSelected] = useState(["buddhism", "shaivism"]);

  const toggle = (slug) => {
    setSelected((cur) => {
      if (cur.includes(slug)) return cur.length > 1 ? cur.filter((s) => s !== slug) : cur;
      if (cur.length >= 3) return [...cur.slice(1), slug];
      return [...cur, slug];
    });
  };

  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="compare-page">
      <section className="py-14 lg:py-20 border-b border-indic-gold/15">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <Eyebrow>Comparative Study</Eyebrow>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-semibold text-amber-50">Compare Traditions</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-300/90 leading-relaxed">
            Set the great Indic traditions side by side across the questions they each answer. Choose two or three to compare.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {ALL.map((slug) => {
              const on = selected.includes(slug);
              return (
                <button key={slug} onClick={() => toggle(slug)} data-testid={`compare-pick-${slug}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${on ? "text-indic-indigo" : "border border-indic-card-border text-slate-300 hover:border-indic-gold/40"}`}
                  style={on ? { background: TRADITIONS[slug].accent } : {}}>
                  {on && <Check size={14} />} {TRADITIONS[slug].name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          {/* header row */}
          <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: `minmax(150px, 1fr) repeat(${selected.length}, minmax(0, 2fr))` }}>
            <div />
            {selected.map((slug) => {
              const t = TRADITIONS[slug];
              return (
                <div key={slug} className="rounded-sm border border-indic-card-border overflow-hidden" data-testid={`compare-col-${slug}`}>
                  <div className="relative h-24">
                    <img src={t.img} alt={t.name} className="h-full w-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-indic-card to-transparent" />
                    <div className="absolute bottom-2 left-3">
                      <span className="font-deva text-sm block" style={{ color: t.accent }}>{t.sanskrit}</span>
                      <Link to={`/traditions/${slug}`} className="font-serif text-xl text-amber-50 hover:text-indic-gold">{t.name}</Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* dimension rows */}
          <div className="space-y-3">
            {COMPARE_DIMENSIONS.map((dim, di) => (
              <Reveal key={dim.key} delay={di * 0.03}>
                <div className="grid gap-4 items-stretch" style={{ gridTemplateColumns: `minmax(150px, 1fr) repeat(${selected.length}, minmax(0, 2fr))` }}>
                  <div className="flex items-center">
                    <span className="font-serif text-lg text-indic-gold">{dim.label}</span>
                  </div>
                  {selected.map((slug) => (
                    <div key={slug} className="rounded-sm border border-indic-card-border bg-indic-card p-4" data-testid={`compare-cell-${slug}-${dim.key}`}>
                      <p className="text-sm text-slate-300/90 leading-relaxed">{COMPARE[slug][dim.key]}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/scholar" className="inline-flex items-center gap-2 rounded-sm bg-indic-gold px-6 py-3 font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="compare-scholar-cta">
              Ask INDIC Scholar to compare further <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
